import { useEffect, useState, type CSSProperties } from "react";
import type { EventTheme } from "@tih/shared";
import { DEFAULT_FLAME_THEME } from "@tih/shared";
import { resolveThemePreviewVars } from "../../lib/event-theme";

type Props = {
  theme: EventTheme;
  onChange: (theme: EventTheme) => void;
  onReextract: () => void;
  extracting?: boolean;
  disabled?: boolean;
  /** Past events cannot apply accents on the live site. */
  lockApplyWhenLive?: boolean;
};

type SlotKey = "primary" | "highlight" | "deep" | "background";

const SLOTS: { key: SlotKey; label: string; useKey: keyof EventTheme; colorKey: keyof EventTheme }[] = [
  { key: "primary", label: "Primary", useKey: "usePrimary", colorKey: "primary" },
  { key: "highlight", label: "Highlight", useKey: "useHighlight", colorKey: "highlight" },
  { key: "deep", label: "Deep", useKey: "useDeep", colorKey: "deep" },
  { key: "background", label: "Background", useKey: "useBackground", colorKey: "background" },
];

const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;

function normalizeHex(raw: string): string | null {
  const trimmed = raw.trim();
  if (!HEX_PATTERN.test(trimmed)) return null;
  return trimmed.toUpperCase();
}

function HexColorInput({
  value,
  label,
  disabled,
  onCommit,
}: {
  value: string;
  label: string;
  disabled?: boolean;
  onCommit: (hex: string) => void;
}) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  function commit(raw: string) {
    const hex = normalizeHex(raw);
    if (hex) {
      onCommit(hex);
      setDraft(hex);
      return;
    }
    setDraft(value);
  }

  return (
    <input
      type="text"
      className="admin-accent-swatch-hex-input"
      value={draft}
      disabled={disabled}
      spellCheck={false}
      autoComplete="off"
      aria-label={`${label} hex color`}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit((e.target as HTMLInputElement).value);
        }
      }}
    />
  );
}

export function EventAccentPicker({
  theme,
  onChange,
  onReextract,
  extracting = false,
  disabled = false,
  lockApplyWhenLive = false,
}: Props) {
  const previewStyle = resolveThemePreviewVars(theme) as CSSProperties;

  function toggleSlot(useKey: keyof EventTheme) {
    onChange({ ...theme, [useKey]: !theme[useKey] });
  }

  function setColor(colorKey: keyof EventTheme, value: string) {
    const hex = normalizeHex(value);
    if (hex) onChange({ ...theme, [colorKey]: hex });
  }

  return (
    <div className="admin-accent-picker">
      <div className="admin-accent-picker-header">
        <span className="admin-accent-picker-title">Poster palette</span>
        <div className="admin-accent-picker-actions">
          <button
            type="button"
            className="btn btn-ghost"
            disabled={disabled || extracting}
            onClick={onReextract}
          >
            {extracting ? "Extracting…" : "Re-extract"}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={disabled}
            onClick={() => onChange({ ...DEFAULT_FLAME_THEME })}
          >
            Reset to flame
          </button>
        </div>
      </div>

      <p className="admin-accent-picker-hint">
        Colors detected from the poster. Toggle each slot on or off, then fine-tune with the picker or hex field.
      </p>

      <div className="admin-accent-swatches">
        {SLOTS.map(({ key, label, useKey, colorKey }) => {
          const color = theme[colorKey] as string;
          const enabled = theme[useKey] as boolean;
          return (
            <div
              key={key}
              className={`admin-accent-swatch${enabled ? "" : " admin-accent-swatch--off"}`}
            >
              <label className="admin-accent-swatch-use">
                <input
                  type="checkbox"
                  checked={enabled}
                  disabled={disabled}
                  onChange={() => toggleSlot(useKey)}
                />
                <span className="admin-accent-swatch-label">{label}</span>
              </label>
              <label className="admin-accent-swatch-color-btn" title={`Pick ${label.toLowerCase()} color`}>
                <span
                  className="admin-accent-swatch-color"
                  style={{ background: color }}
                  aria-hidden
                />
                <input
                  type="color"
                  className="admin-accent-swatch-picker"
                  value={color}
                  disabled={disabled}
                  onChange={(e) => setColor(colorKey, e.target.value)}
                  aria-label={`${label} color picker`}
                />
              </label>
              <HexColorInput
                value={color}
                label={label}
                disabled={disabled}
                onCommit={(hex) => setColor(colorKey, hex)}
              />
            </div>
          );
        })}
      </div>

      <label className="admin-accent-live-toggle">
        <input
          type="checkbox"
          checked={theme.applyWhenLive}
          disabled={disabled || lockApplyWhenLive}
          onChange={(e) => onChange({ ...theme, applyWhenLive: e.target.checked })}
        />
        <span>
          {lockApplyWhenLive
            ? "Accents not applied — event is in the past"
            : "Apply accents when this event is live on site"}
        </span>
      </label>

      <div className="admin-accent-preview" style={previewStyle}>
        <span className="admin-accent-preview-bg" aria-hidden />
        <span className="admin-accent-preview-eyebrow">Preview</span>
        <a href="#preview" className="admin-accent-preview-link" onClick={(e) => e.preventDefault()}>
          Nav link
        </a>
        <button type="button" className="btn btn-primary admin-accent-preview-btn">
          Primary button
        </button>
      </div>
    </div>
  );
}
